import { User, UserRole } from '../types/auth'
import { API_ENDPOINTS } from '../config/api'

const STORAGE_KEY_SESSION = 'ldh_trans_session'

// Authenticate user via backend API
export const login = async (email: string, password: string): Promise<{ user: User; token: string }> => {
  try {
    const response = await fetch(API_ENDPOINTS.auth.login, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Login failed')
    }

    const data = await response.json()

    // Store session in localStorage
    const session = {
      user: data.user,
      token: data.token,
      timestamp: Date.now()
    }
    localStorage.setItem(STORAGE_KEY_SESSION, JSON.stringify(session))

    return { user: data.user, token: data.token }
  } catch (error) {
    if (error instanceof Error) {
      throw error
    }
    throw new Error('Network error. Please check if the server is running.')
  }
}

// Logout
export const logout = () => {
  localStorage.removeItem(STORAGE_KEY_SESSION)
}

// Get current session
export const getSession = (): { user: User; token: string } | null => {
  const session = localStorage.getItem(STORAGE_KEY_SESSION)
  if (!session) return null

  try {
    const parsed = JSON.parse(session)
    // Check if session is less than 24 hours old
    const age = Date.now() - parsed.timestamp
    if (age > 24 * 60 * 60 * 1000) {
      logout()
      return null
    }
    return { user: parsed.user, token: parsed.token }
  } catch {
    return null
  }
}

// Verify token with backend
export const verifyToken = async (token: string): Promise<User | null> => {
  try {
    const response = await fetch(API_ENDPOINTS.auth.verify, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      return null
    }

    const data = await response.json()
    return data.user
  } catch {
    return null
  }
}

// Check if user has required role
export const hasRole = (user: User | null, allowedRoles: UserRole[]): boolean => {
  if (!user) return false
  return allowedRoles.includes(user.role)
}

// Get all users (for admin/manager)
export const getUsers = async (): Promise<User[]> => {
  const session = getSession()
  if (!session) {
    throw new Error('Not authenticated')
  }

  // For now, return empty array (will implement later)
  return []
}
